import { ReactNode, createContext, useCallback, useMemo, useContext } from 'react';
import { Center, Spinner } from '@chakra-ui/react';
import { Navigate, useLocation } from 'react-router-dom';
import { trpcAdmin } from './lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { buildLoginRedirectUrl } from '@duke-hq/libauth-client/safeRedirectParam';

type AuthContextType = {
  user: RouterOutputs['me'] | undefined;
  signOut: () => Promise<void>;
};

// cast the default empty object as our context type, as we will ensure not to
// populate the context value without a valid type
export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProviderAdmin({ children }: { children: ReactNode }) {
  const logout = trpcAdmin.auth.logout.useMutation();
  const location = useLocation();
  const { data: user, isLoading } = trpcAdmin.me.useQuery(undefined, {
    retry: false,
  });

  const signOut = useCallback(async () => {
    await logout.mutateAsync();
    window.location.replace('/login');
  }, [logout]);

  const value: AuthContextType = useMemo(
    () => ({
      user,
      signOut,
    }),
    [user, signOut],
  );

  if (isLoading || user === undefined) {
    return (
      <Center h="100vh">
        <Spinner size={'xl'} />
      </Center>
    );
  }

  if (user === null && location.pathname !== '/login' && location.pathname !== '/magiclink') {
    return (
      <Navigate to={buildLoginRedirectUrl('/login', location.pathname, location.search)} replace />
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * use this alias anywhere in the app to get the current user
 *
 * ```ts
 * const session = useAuthAdmin();
 * ```
 */
export function useAuthAdmin() {
  return useContext(AuthContext);
}
